import { Color } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { ContextMenu, Icon } from '@opallabs/lapidary'
import { AllowedEvents, TrackEvent } from '@opallabs/quanta'
import type { ContextMenuCallbacks } from '../../cardContext'
import type { PartialContent, PartialMoment } from '../../momentTypes'

type ContentCardContextMenuProps = {
  content: PartialContent
  contextMenuCallbacks: ContextMenuCallbacks
  moment: PartialMoment | undefined
  setIsRenamePopoverOpen: (open: boolean) => void
  track: TrackEvent
}

export const ContentCardContextMenu: React.FC<
  React.PropsWithChildren<ContentCardContextMenuProps>
> = ({
  children,
  content,
  contextMenuCallbacks,
  moment,
  setIsRenamePopoverOpen,
  track,
}) => {
  const theme = useTheme()
  const disableAllOptions = !!content.isSaving || !!moment?.isUpdatingContent
  const contentType = content.service?.name
  // Mirrors the content route used by `navigateToContent` on card click.
  const contentUrl =
    moment && moment.primaryBrand?.slug && content.id
      ? `${window.location.origin}/moments/${moment.primaryBrand.slug}/${moment.id}/posts/${content.id}`
      : undefined

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>{children}</ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content style={{ zIndex: 999 }}>
          <ContextMenu.Label
            css={{
              color: Color[theme].Text.Gray,
              opacity: 1,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <Icon
              ariaHidden
              glyph="content-cutout"
              color={Color[theme].Text.Gray}
              height={16}
              useHypernova={true}
              width={16}
            />
            {content?.name}
          </ContextMenu.Label>
          <ContextMenu.Separator />

          <ContextMenu.Item
            disabled={!contentUrl}
            onSelect={() => {
              track(AllowedEvents.click, {
                action: 'open content in new tab',
                id: content.id,
                contentType,
              })
              if (contentUrl) window.open(contentUrl, '_blank')
            }}
          >
            Open Content in New Tab
          </ContextMenu.Item>

          <ContextMenu.Separator />

          <ContextMenu.Item
            disabled={disableAllOptions}
            onSelect={() => {
              track(AllowedEvents.click, {
                action: 'rename content',
                id: content.id,
                contentType,
              })
              setIsRenamePopoverOpen(true)
            }}
          >
            Rename content
          </ContextMenu.Item>

          <ContextMenu.Item
            disabled={disableAllOptions}
            onSelect={() => {
              track(AllowedEvents.click, {
                action: 'duplicate content',
                id: content.id,
                contentType,
              })
              contextMenuCallbacks.duplicateContent(content, moment)
            }}
          >
            Duplicate content
          </ContextMenu.Item>

          <ContextMenu.Separator />

          <ContextMenu.Item
            onSelect={() => {
              track(AllowedEvents.click, {
                action: 'delete content',
                id: content.id,
                contentType,
              })
              contextMenuCallbacks.deleteContent(content.id, moment)
            }}
            css={{ color: Color[theme].Text.Warning }}
          >
            Delete content
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}
